import { createSignal, Show, For, Switch, Match } from "solid-js";
import { saveRegistration } from "~/_server";
import rawSchemes from "~/data/schemes.json";

const validate = ( data ) => {
	const errors = [];

	if ( !data?.firstName?.trim() || !data?.lastName?.trim() ) {
		errors.push( { type: "error", msg: "We need your first name and your last name, please." } );
	}

	if ( !data.email?.trim().match( /^[^@]+@[^@]+\.[^@]+$/ ) ) {
		errors.push( { type: "error", msg: "We need a valid email address." } );
	}

	if ( !data?.companyName?.trim() || !data?.postcode?.trim() ) {
		errors.push( {
			type: "error",
			msg: "We need your company name (or your own name if you are a sole trader), and your post code.",
		} );
	}

	return errors;
};

export const Register = () => {
	const [submitting, setSubmitting] = createSignal( false );
	const [complete, setComplete] = createSignal( false );
	const [toast, setToast] = createSignal<any[{ string; string }]>( [] );

	const handleSubmit = async ( e ) => {
		e.preventDefault();
		e.stopPropagation();

		setSubmitting( true );

		const form = e.currentTarget;
		const formSubmission = Object.fromEntries( new FormData( form ).entries() );
		const errors = validate( formSubmission );

		if ( errors.length ) {
			setToast( errors );
			setTimeout( () => setToast( [] ), 37500 );
		} else {
			const r = await saveRegistration( formSubmission );

			if ( !r.success ) {
				setToast( [{ type: "error", msg: r.err }] );
			} else {
				setComplete( true );
			}
		}

		setSubmitting( false );
	};

	return (
		<>
			<Switch>
				<Match when={complete()}>
					<h2 class="text-4xl font-semibold text-gray-900">✅ Thank you!</h2>
					<p class="mt-2 text-lg text-gray-600">
						Your application has been received and we will be in touch very soon.
					</p>
				</Match>

				<Match when={!complete()}>
					<form onSubmit={handleSubmit} class="relative my-12 p-4">
						<Show when={submitting()}>
							<div class="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-black/10 backdrop-blur-sm">
								<div class="h-30 w-30 animate-spin rounded-full border-4 border-white/50 border-t-transparent"></div>
							</div>
						</Show>

						<div class="space-y-12">
							<div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
								<div>
									<h2 class="text-base/7 font-semibold text-gray-900">
										Personal Information
									</h2>
									<p class="mt-1 text-sm/6 text-gray-600">
										Please use a permanent email address where you can regularly
										receive email.
									</p>
								</div>

								<div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
									<div class="sm:col-span-3">
										<label
											for="firstName"
											class="block text-sm/6 font-medium text-gray-900">
											First name
										</label>
										<div class="mt-2">
											<input
												id="firstName"
												type="text"
												name="firstName"
												autocomplete="given-name"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>
									<div class="sm:col-span-3">
										<label
											for="lastName"
											class="block text-sm/6 font-medium text-gray-900">
											Last name
										</label>
										<div class="mt-2">
											<input
												id="lastName"
												type="text"
												name="lastName"
												autocomplete="family-name"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-4">
										<label
											for="email"
											class="block text-sm/6 font-medium text-gray-900">
											Email address
										</label>
										<div class="mt-2">
											<input
												id="email"
												type="email"
												name="email"
												autocomplete="email"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>
								</div>
							</div>

							<div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
								<div>
									<h2 class="text-base/7 font-semibold text-gray-900">
										Memberships/Qualifications
									</h2>
									<p class="mt-1 text-sm/6 text-gray-600">
										Please provide membership numbers for any of these
										organisations.
									</p>
								</div>
								<div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
									<div class="sm:col-span-3 sm:col-start-1">
										<label
											for="ARB"
											class="block text-sm/6 font-medium text-gray-900">
											ARB
										</label>
										<div class="mt-2">
											<input
												id="ARB"
												type="text"
												name="ARB"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-3">
										<label
											for="RIAS"
											class="block text-sm/6 font-medium text-gray-900">
											RIAS
										</label>
										<div class="mt-2">
											<input
												id="RIAS"
												type="text"
												name="RIAS"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-3">
										<label
											for="CIBSE"
											class="block text-sm/6 font-medium text-gray-900">
											CIBSE<br/><span class="text-xs">(Corporate Member or Low Carbon Consultant)</span>
										</label>
										<div class="mt-2">
											<input
												id="CIBSE"
												type="text"
												name="CIBSE"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-3">
										<label
											for="MCIAT"
											class="block text-sm/6 font-medium text-gray-900">
											CIAT<br/><span class="text-xs">(for those that are MCIAT or FTCIAT only)</span>
										</label>
										<div class="mt-2">
											<input
												id="MCIAT"
												type="text"
												name="MCIAT"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="col-span-full">
										<div class="flex gap-3">
											<div class="flex h-6 shrink-0 items-center">
												<div class="group grid size-4 grid-cols-1">
													<input
														id="contructionPro"
														type="checkbox"
														name="contructionProl"
														aria-describedby="contructionPro-description"
														class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
													/>
													<svg
														viewBox="0 0 14 14"
														fill="none"
														class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
														<path
															d="M3 8L6 11L11 3.5"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
															class="opacity-0 group-has-checked:opacity-100"
														/>
														<path
															d="M3 7H11"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
															class="opacity-0 group-has-indeterminate:opacity-100"
														/>
													</svg>
												</div>
											</div>
											<div class="text-sm/6">
												<label
													for="contructionPro"
													class="font-medium text-gray-900">
													Construction Professional{" "}
													<span class="font-light">
														Non-ARB/non-MCIAT/non-TCIAT/non-CIBSE
													</span>
												</label>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
								<div>
									<h2 class="text-base/7 font-semibold text-gray-900">
										Company Information
									</h2>
									<p class="mt-1 text-sm/6 text-gray-600">
										Please provide details of the company that would become your
										Approved Body.
									</p>
								</div>
								<div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
									<div class="col-span-full">
										<label
											for="companyName"
											class="block text-sm/6 font-medium text-gray-900">
											Company Name
										</label>
										<div class="mt-2">
											<input
												id="companyName"
												type="text"
												name="companyName"
												autocomplete="organization"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="col-span-full">
										<label
											for="addr1"
											class="block text-sm/6 font-medium text-gray-900">
											Street address
										</label>
										<div class="mt-2">
											<input
												id="addr1"
												type="text"
												name="addr1"
												autocomplete="address-line1"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-2 sm:col-start-1">
										<label
											for="city"
											class="block text-sm/6 font-medium text-gray-900">
											City
										</label>
										<div class="mt-2">
											<input
												id="city"
												type="text"
												name="city"
												autocomplete="address-level2"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-2">
										<label
											for="region"
											class="block text-sm/6 font-medium text-gray-900">
											County
										</label>
										<div class="mt-2">
											<input
												id="region"
												type="text"
												name="region"
												autocomplete="address-level1"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>

									<div class="sm:col-span-2">
										<label
											for="postcode"
											class="block text-sm/6 font-medium text-gray-900">
											Postcode
										</label>
										<div class="mt-2">
											<input
												id="postcode"
												type="text"
												name="postcode"
												autocomplete="postal-code"
												class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
											/>
										</div>
									</div>
								</div>
							</div>

							<div class="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12">
								<div>
									<h2 class="text-base/7 font-semibold text-gray-900">
										Initial Scheme
									</h2>
									<p class="mt-1 text-sm/6 text-gray-600">
										You will be able to add additional memberships once your
										registration has been validated.
									</p>
								</div>

								<div class="max-w-2xl space-y-10 md:col-span-2">
									<fieldset>
										<div class="mt-6 space-y-6">
											<div class="grid grid-cols-[30px_1fr]">
												<input
													id="s6Domestic"
													value="s6Domestic"
													type="radio"
													name="targetScheme"
													checked
													class="relative size-6 appearance-none rounded-full border border-gray-400 bg-white before:absolute before:inset-1 before:rounded-full not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
												/>
												<label
													for="s6Domestic"
													class="ml-3 block text-sm/6 font-medium text-gray-900">
													Section 6 (Energy) for Domestic Buildings
												</label>

												<span class="col-start-2 subscription-description">
													This Domestic-only membership subscription
													allows you to create Section 6 certificates for
													domestic plots etc. Currently, membership is{" "}
													<span class="font-medium">
														£{rawSchemes.s6Domestic.renewalFee}pa
													</span>{" "}
													with a joining fee of{" "}
													<span class="font-semibold">
														£{rawSchemes.s6Domestic.joiningFee}
													</span>
													.
												</span>
											</div>

											<div class="grid grid-cols-[30px_1fr]">
												<input
													id="s6NonDomestic"
													value="s6NonDomestic"
													type="radio"
													name="targetScheme"
													class="relative size-6 appearance-none rounded-full border border-gray-400 bg-white before:absolute before:inset-1 before:rounded-full not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
												/>
												<label
													for="s6NonDomestic"
													class="ml-3 block text-sm/6 font-medium text-gray-900">
													Section 6 (Energy) for Non-Domestic Buildings
												</label>

												<span class="col-start-2 subscription-description">
													Our Non-Domestic membership subscription allows
													you to create Section 6 certificates for
													offices, factorie and other non-domestic
													buildings. Currently, membership is £
													{rawSchemes.s6NonDomestic.renewalFee}pa with a
													joining fee of £
													{rawSchemes.s6NonDomestic.joiningFee}.
												</span>
											</div>

											<div class="grid grid-cols-[30px_1fr]">
												<input
													id="s6NonDomesticAdv"
													value="s6NonDomesticAdv"
													type="radio"
													name="targetScheme"
													class="relative size-6 appearance-none rounded-full border border-gray-400 bg-white before:absolute before:inset-1 before:rounded-full not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
												/>
												<label
													for="s6NonDomesticAdv"
													class="ml-3 block text-sm/6 font-medium text-gray-900">
													Section 6 (Energy) for Non-Domestic Buildings -
													Advanced
												</label>

												<span class="col-start-2 subscription-description">
													The Non-Domestic (Advanced) membership
													subscription allows you to create Section 6
													certificates for offices, factorie and other
													non-domestic buildings. Currently, membership is
													£{rawSchemes.s6NonDomesticAdv.renewalFee}pa with
													a joining fee of £
													{rawSchemes.s6NonDomesticAdv.joiningFee}.
												</span>
											</div>

											<div class="grid grid-cols-[30px_1fr]">
												<input
													id="CPD"
													value="CPD"
													type="radio"
													name="targetScheme"
													class="relative size-6 appearance-none rounded-full border border-gray-400 bg-white before:absolute before:inset-1 before:rounded-full not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
												/>
												<label
													for="CPD"
													class="ml-3 block text-sm/6 font-medium text-gray-900">
													CPD
												</label>

												<span class="col-start-2 subscription-description">
													Our CPD membership is for those who wish to
													improve their skills in Enery Certificatio, but
													are not yet ready to certify themselves. The
													one-off membership fee (no renewal) is currently
													£{rawSchemes.cpd.joiningFee}. RIAS members will
													receive a 20% discount on that fee.
												</span>
											</div>

											<p class="text-sm text-center italic">
												All fees are shown excluding VAT. VAT (currently
												20%) will be added.
											</p>
										</div>
									</fieldset>
								</div>
							</div>

							<div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
								<div>
									<h2 class="text-base/7 font-semibold text-gray-900">
										Terms & Conditions
									</h2>
									<p class="mt-1 text-sm/6 text-gray-600"></p>
								</div>

								<div class="max-w-2xl space-y-10 md:col-span-2">
									<fieldset>
										<div class="mt-6 space-y-6">
											<div class="flex gap-3">
												<div class="flex h-6 shrink-0 items-center">
													<div class="group grid size-4 grid-cols-1">
														<input
															id="terms"
															type="checkbox"
															name="terms"
															checked
															required
															aria-describedby="terms-description"
															class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
														/>
														<svg
															viewBox="0 0 14 14"
															fill="none"
															class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
															<path
																d="M3 8L6 11L11 3.5"
																stroke-width="2"
																stroke-linecap="round"
																stroke-linejoin="round"
																class="opacity-0 group-has-checked:opacity-100"
															/>
															<path
																d="M3 7H11"
																stroke-width="2"
																stroke-linecap="round"
																stroke-linejoin="round"
																class="opacity-0 group-has-indeterminate:opacity-100"
															/>
														</svg>
													</div>
												</div>
												<div class="text-sm/6">
													<label
														for="terms"
														class="font-medium text-gray-900">
														I accept the terms & conditions of use of
														this website
													</label>
												</div>
											</div>
										</div>
									</fieldset>
								</div>
							</div>

							<div class="mt-6 flex items-center justify-end gap-x-6">
								<button
									type="submit"
									class="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
									Apply for Memberhsip
								</button>
							</div>
						</div>
					</form>
				</Match>
			</Switch>

			<div
				aria-live="assertive"
				class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6">
				<div class="flex w-full flex-col items-center space-y-4 sm:items-end">
					<For each={toast()}>
						{( t ) => (
							<div
								class="pointer-events-auto w-full max-w-sm translate-y-0 transform rounded-lg opacity-100 shadow-lg outline-1 outline-black/5 transition duration-300 ease-out sm:translate-x-0 starting:translate-y-2 starting:opacity-0 starting:sm:translate-x-2 starting:sm:translate-y-0 p-4"
								classList={{
									"bg-white": t.type == "success",
									"bg-red-100": t.type == "error",
								}}>
								<div class="flex items-start">
									<div class="shrink-0">
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="1.5"
											data-slot="icon"
											aria-hidden="true"
											class="size-6"
											classList={{
												"text-green-400": t.type == "success",
												"text-red-400": t.type == "error",
											}}>
											<path
												d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</div>
									<div class="ml-3 w-0 flex-1">
										<span class="block my-0 text-sm font-medium text-gray-900 capitalize">
											{t.type}
										</span>
										<span class="block mt-1 text-sm text-gray-500">
											{t.msg}
										</span>
									</div>
									{/*
										<div class="ml-4 flex shrink-0">
											<button type="button" class="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-sky-600">
												<span class="sr-only">Close</span>
												<svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" class="size-5">
													<path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
												</svg>
											</button>
										</div>
										*/}
								</div>
							</div>
						)}
					</For>
				</div>
			</div>
		</>
	);
};
